import { toast } from "$global-providers/toast-provider";
import { Box, Button, styled, TextField } from "@material-ui/core";
import { type FC, useState } from "react";
import { rem } from "../../_lib/epocus-palette";
import { useEpocusUpdateUserPersonalInfoMutation } from "./__generated__/epocus-settings-operations.gql";
import { SettingsCard, SettingsCardLabel } from "./settings-card";

const FieldsRow = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: rem(4),
});

export const PersonalInfoCard: FC<{
  userId: string;
  firstname?: string | null;
  middlename?: string | null;
  lastname?: string | null;
}> = ({ userId, firstname, middlename, lastname }) => {
  const [firstName, setFirstName] = useState(firstname ?? "");
  const [middleName, setMiddleName] = useState(middlename ?? "");
  const [lastName, setLastName] = useState(lastname ?? "");
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);

  const [updateUser, { loading }] = useEpocusUpdateUserPersonalInfoMutation({
    onCompleted: () => toast.success("Personal information saved"),
    onError: (e) => toast.error(`Couldn't save: ${e.message}`),
  });

  const handleSave = async () => {
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const isFirstNameInvalid = trimmedFirstName.length === 0;
    const isLastNameInvalid = trimmedLastName.length === 0;

    setFirstNameError(isFirstNameInvalid);
    setLastNameError(isLastNameInvalid);

    if (isFirstNameInvalid || isLastNameInvalid) {
      return;
    }

    await updateUser({
      variables: {
        input: {
          _id: userId,
          person: {
            firstname: trimmedFirstName,
            middlename: middleName.trim() || undefined,
            lastname: trimmedLastName,
          },
        },
      },
    });
  };

  return (
    <SettingsCard>
      <SettingsCardLabel>Personal information</SettingsCardLabel>
      <FieldsRow>
        <TextField
          id="epocus-settings-firstname"
          variant="outlined"
          margin="dense"
          fullWidth
          required
          label="First name"
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
            setFirstNameError(false);
          }}
          error={firstNameError}
          helperText={firstNameError ? "First name is required" : undefined}
        />
        <TextField
          id="epocus-settings-middlename"
          variant="outlined"
          margin="dense"
          fullWidth
          label="Middle name"
          value={middleName}
          onChange={(e) => setMiddleName(e.target.value)}
        />
        <TextField
          id="epocus-settings-lastname"
          variant="outlined"
          margin="dense"
          fullWidth
          required
          label="Last name"
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
            setLastNameError(false);
          }}
          error={lastNameError}
          helperText={lastNameError ? "Last name is required" : undefined}
        />
      </FieldsRow>
      <Button
        variant="contained"
        color="primary"
        disabled={loading}
        style={{ alignSelf: "flex-end" }}
        onClick={handleSave}
      >
        Save
      </Button>
    </SettingsCard>
  );
};
